// Pie de paso con las tres posiciones del mock: izquierda «← Volver», CENTRO la salida,
// derecha la acción primaria (`layout.css:192`, `justify-content: space-between`).
//
// La salida al centro es una regla constante en las 8 pantallas del mock que la tienen, y el
// paso 10 la hace explícita con `flex-1 justify-center`. Aquí se centra de verdad para que las
// 10 pantallas coincidan entre sí, no solo con el mock.
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { WizardCardFoot } from './WizardCard';
import { useWizardExit } from './WizardExit';
import type { WizardStepSlug } from '../types';

export function WizardBackLink({
    sessionId,
    step,
    label = '← Volver',
}: {
    sessionId: string;
    step: WizardStepSlug;
    label?: string;
}) {
    return (
        <Link
            to="/wizard/s/$sessionId/$step"
            params={{ sessionId, step }}
            className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
            {label}
        </Link>
    );
}

/**
 * «Guardar y salir» del mock. Se pinta solo donde el paso admite prospecto; en los demás el
 * centro queda vacío y las otras dos posiciones no se mueven.
 */
export function SaveAndExitButton({ className }: { className?: string }) {
    const exit = useWizardExit();
    if (!exit?.canSaveProspect) return null;
    return (
        <button
            type="button"
            onClick={exit.openProspectDrawer}
            className={
                className ??
                'text-[13px] text-muted-foreground transition-colors hover:text-foreground'
            }
        >
            Guardar y salir
        </button>
    );
}

/**
 * Pie de un paso. `back` y `children` (la acción primaria) son opcionales: un estado sin acción
 * conserva el pie con sus otras posiciones en vez de quedarse sin él, que es lo que hacía que
 * la card se viera distinta según el estado.
 *
 * `showExit` lo apaga la pantalla que no quiere ofrecer salida todavía —la espera de la cuenta en
 * sus primeros segundos—. El hueco central se conserva para que las otras dos no se muevan.
 * `exit` la reemplaza por otra donde el paso ofrece su propia salida.
 */
export function WizardFootNav({
    back,
    children,
    exit,
    showExit = true,
}: {
    back?: ReactNode;
    children?: ReactNode;
    /** Reemplaza la salida del centro. La Bienvenida ofrece la suya: salir sin activar la recarga. */
    exit?: ReactNode;
    showExit?: boolean;
}) {
    return (
        <WizardCardFoot>
            {/* Los dos laterales reparten el sobrante en partes iguales, así la salida cae en el
                centro REAL del pie y no se corre según lo ancho que sea el botón primario. */}
            <div className="flex items-center justify-center sm:flex-1 sm:justify-start">
                {back}
            </div>
            <div className="flex items-center justify-center sm:flex-none">
                {showExit ? (exit ?? <SaveAndExitButton />) : null}
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-1 sm:flex-row sm:items-center sm:justify-end">
                {children}
            </div>
        </WizardCardFoot>
    );
}
